
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useMechanicShare } from '@/contexts/MechanicShareContext';
import { useCar, Car, CarIssue } from '@/contexts/CarContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { AlertTriangle, Wrench, AlertCircle, CheckCircle } from 'lucide-react';
import MechanicRepairForm from './MechanicRepairForm';
import MechanicSymptomForm from './MechanicSymptomForm';
import { format } from 'date-fns';

const MechanicEntry: React.FC = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const navigate = useNavigate();
  const { getShareLinkByToken, isLinkValid } = useMechanicShare();
  const { cars } = useCar();
  
  const [car, setCar] = useState<Car | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openDialog, setOpenDialog] = useState<'repair' | 'symptom' | null>(null);
  
  useEffect(() => {
    if (!token) {
      setError('No access token provided');
      setLoading(false);
      return;
    }
    
    const shareLink = getShareLinkByToken(token);
    if (!shareLink) {
      setError('Invalid access link');
      setLoading(false);
      return;
    }
    
    if (!isLinkValid(token)) {
      setError('This link has expired');
      setLoading(false);
      return;
    }
    
    const carData = cars.find(c => c.id === shareLink.carId);
    if (!carData) {
      setError('Car not found');
      setLoading(false);
      return;
    }
    
    setCar(carData);
    setLoading(false);
  }, [token, getShareLinkByToken, cars, isLinkValid]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-pulse text-lg">Loading...</div>
      </div>
    );
  }

  if (error || !car) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-4">
        <AlertTriangle className="text-red-500 mb-4" size={48} />
        <h1 className="text-2xl font-bold text-center mb-2">Access Error</h1>
        <p className="text-center text-gray-600 mb-6">{error || 'Something went wrong'}</p>
        <div className="text-sm text-gray-500 mt-8">
          If you believe this is a mistake, please contact the car owner for a new link.
        </div>
      </div>
    );
  }

  const shareLink = getShareLinkByToken(token);
  const isEditable = shareLink?.isEditable || false;
  const expiresAt = shareLink ? new Date(shareLink.expiresAt) : new Date();
  const today = new Date();
  const expiresInHours = Math.ceil((expiresAt.getTime() - today.getTime()) / (1000 * 60 * 60));
  
  // Get recent issues (unresolved)
  const openIssues = car.issues?.filter(issue => 
    issue.status === 'new' || issue.status === 'partially_resolved'
  ) || [];

  return (
    <div className="max-w-3xl mx-auto p-4 pb-20">
      <Card className="mb-6 border-lime/20 bg-lime/5 shadow-md">
        <CardHeader>
          <CardTitle className="text-xl text-center">
            You've been invited to view and update repair history
          </CardTitle>
          <CardDescription className="text-center text-base">
            {car.brand} {car.model} {car.licensePlate ? `• ${car.licensePlate}` : ''}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col space-y-2">
            <div className="bg-background rounded-lg p-4 border">
              <h3 className="font-bold text-lg">
                {car.nickname || `${car.brand} ${car.model}`}
              </h3>
              <p className="text-gray-500">
                {car.nickname ? `${car.brand} ${car.model}` : ''} • {car.year} • {car.licensePlate || 'No plate'}
              </p>
              {car.vin && <p className="text-xs text-gray-500 mt-1">VIN: {car.vin}</p>}
            </div>
            
            <div className="flex items-center text-amber-600 text-sm mt-2">
              <AlertCircle size={16} className="mr-1" />
              This link will expire in {expiresInHours} hour{expiresInHours !== 1 ? 's' : ''}
            </div>

            {!isEditable && (
              <div className="flex items-center text-gray-600 text-sm">
                <AlertCircle size={16} className="mr-1" />
                This is a view-only link. You cannot add repair records.
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {openIssues.length > 0 && (
        <Card className="mb-6 border-amber-200">
          <CardHeader>
            <CardTitle className="text-lg flex items-center">
              <AlertTriangle size={18} className="mr-2 text-amber-500" />
              Open Issues ({openIssues.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {openIssues.slice(0, 3).map((issue) => (
                <div key={issue.id} className="p-3 bg-amber-50 dark:bg-amber-950/20 rounded-md border border-amber-100 dark:border-amber-900">
                  <h4 className="font-medium">{issue.title}</h4>
                  <p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-2">{issue.description}</p>
                  <div className="text-xs text-gray-500 mt-1">
                    Reported: {format(new Date(issue.date), 'MMM d, yyyy')}
                  </div>
                </div>
              ))}
              {openIssues.length > 3 && (
                <div className="text-sm text-gray-500 italic text-center">
                  + {openIssues.length - 3} more open issues
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {car.issues && car.issues.length > 0 && openIssues.length === 0 && (
        <div className="mb-6 flex items-center justify-center py-4 text-gray-500">
          <CheckCircle size={16} className="mr-2 text-green-500" />
          No open issues at the moment
        </div>
      )}

      {isEditable && (
        <div className="grid grid-cols-1 gap-4 mt-8">
          <Button
            size="lg" 
            className="h-16 text-lg font-medium bg-lime shadow-md hover:bg-lime/90 text-black"
            onClick={() => setOpenDialog('repair')}
          >
            <Wrench className="mr-2" size={20} />
            Log New Repair
          </Button>
          
          <Button
            size="lg"
            variant="outline"
            className="h-16 text-lg font-medium border-lime text-lime hover:bg-lime/10 hover:text-lime"
            onClick={() => setOpenDialog('symptom')}
          >
            <AlertCircle className="mr-2" size={20} />
            Log New Symptom
          </Button>
        </div>
      )}

      {/* Repair Form Dialog */}
      <Dialog open={openDialog === 'repair'} onOpenChange={() => setOpenDialog(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Add Repair Record</DialogTitle>
            <DialogDescription>
              Log a repair for {car.nickname || `${car.brand} ${car.model}`}
            </DialogDescription>
          </DialogHeader>
          <MechanicRepairForm 
            token={token} 
            car={car} 
            onSuccess={() => {
              setOpenDialog(null);
              // Optional: navigate away or show success
            }} 
          />
        </DialogContent>
      </Dialog>

      {/* Symptom Form Dialog */}
      <Dialog open={openDialog === 'symptom'} onOpenChange={() => setOpenDialog(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Log New Issue</DialogTitle>
            <DialogDescription>
              Describe the issue with {car.nickname || `${car.brand} ${car.model}`}
            </DialogDescription>
          </DialogHeader>
          <MechanicSymptomForm 
            token={token} 
            car={car} 
            onSuccess={() => {
              setOpenDialog(null);
              // Optional: navigate away or show success
            }} 
          />
        </DialogContent>
      </Dialog>

      <footer className="fixed bottom-0 left-0 w-full bg-white dark:bg-gray-900 border-t p-2 text-center text-xs text-gray-500">
        Powered by On Wheels
      </footer>
    </div>
  );
};

export default MechanicEntry;
